/**
 * Payment Panel Component.
 *
 * Reusable payment recording and history panel for PO and SO detail pages.
 *
 * Key concept: payment METHOD (how — check, wire, card, cash) is independent
 * from funding SOURCE (where the money comes from). Any payment method can
 * optionally be funded from a credit account (line of credit, credit card
 * account, loan). A user might write a check drawn on their LOC, or use a
 * card linked to their LOC — the method and funding source are two separate
 * things.
 */

import { useState } from "react";
import {
  Plus,
  DollarSign,
  CreditCard,
  Building2,
  Banknote,
  ArrowRightLeft,
  FileText,
  Trash2,
  X,
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { toast } from "@/stores/toastStore";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import Card from "@/components/ui/Card";
import LoadingSpinner from "@/components/ui/LoadingSpinner";

interface PaymentAllocation {
  id: string;
  line_id: string;
  line_type: string;
  amount: number;
  notes: string | null;
  created_at: string;
}

interface Payment {
  id: string;
  direction: string;
  entity_type: string;
  entity_id: string;
  entity_label: string | null;
  amount: number;
  currency: string;
  payment_method: string;
  status: string;
  details: Record<string, unknown> | null;
  credit_account_id: string | null;
  notes: string | null;
  recorded_by: string | null;
  payment_date: string;
  created_at: string;
  allocations: PaymentAllocation[];
}

interface LineItem {
  id: string;
  /** Null on an asset-backed sales order line, which takes its name from the asset. */
  description?: string | null;
  asset_name?: string | null;
  sale_price?: number;
  unit_price?: number;
  quantity?: number;
  total_price?: number;
  line_total?: number;
  line_status?: string;
}

interface LineSummaryItem {
  line_id: string;
  total_allocated: number;
  allocation_count: number;
}

interface PaymentSummary {
  entity_type: string;
  entity_id: string;
  total_paid: number;
  payment_count: number;
  remaining: number | null;
}

interface CreditAccount {
  id: string;
  name: string;
  account_type: string;
  current_balance: number;
  credit_limit: number | null;
  is_active: boolean;
}

const METHOD_ICONS: Record<string, React.ElementType> = {
  cash: Banknote,
  check: FileText,
  wire: ArrowRightLeft,
  ach: ArrowRightLeft,
  credit_card: CreditCard,
  financing: Building2,
  other: DollarSign,
};

const METHOD_LABELS: Record<string, string> = {
  cash: "Cash",
  check: "Check",
  wire: "Wire transfer",
  ach: "ACH",
  credit_card: "Credit card",
  financing: "Financing",
  other: "Other",
};

const fmt = (v: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    v,
  );

interface PaymentPanelProps {
  entityType: "purchase_order" | "sales_order";
  entityId: string;
  lineItems?: LineItem[];
}

function PaymentPanel({ entityType, entityId, lineItems }: PaymentPanelProps) {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);

  const { data: payments, isLoading } = useQuery<Payment[]>({
    queryKey: ["payments", entityType, entityId],
    queryFn: async () =>
      (
        await apiClient.get(
          `/api/v1/payments?entity_type=${entityType}&entity_id=${entityId}`,
        )
      ).data,
  });

  const { data: summary } = useQuery<PaymentSummary>({
    queryKey: ["payment-summary", entityType, entityId],
    queryFn: async () =>
      (
        await apiClient.get(
          `/api/v1/payments/summary?entity_type=${entityType}&entity_id=${entityId}`,
        )
      ).data,
  });

  const { data: lineSummary } = useQuery<LineSummaryItem[]>({
    queryKey: ["payment-line-summary", entityType, entityId],
    queryFn: async () =>
      (
        await apiClient.get(
          `/api/v1/payments/line-summary?entity_type=${entityType}&entity_id=${entityId}`,
        )
      ).data,
    enabled: !!lineItems && lineItems.length > 0,
  });

  const lineSummaryMap = new Map(
    (lineSummary || []).map((ls) => [ls.line_id, ls]),
  );

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ["payments", entityType, entityId] });
    qc.invalidateQueries({
      queryKey: ["payment-summary", entityType, entityId],
    });
    qc.invalidateQueries({
      queryKey: ["payment-line-summary", entityType, entityId],
    });
  };

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/v1/payments/${id}`);
    },
    onSuccess: () => {
      invalidateAll();
      toast.success("Payment removed");
    },
    onError: () => toast.error("Failed to remove payment"),
  });

  return (
    <div className="space-y-4">
      {/* Summary bar */}
      {summary && (
        <div className="flex items-center gap-4 flex-wrap">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
              Total paid
            </p>
            <p className="text-lg font-bold text-[var(--text-success)]">
              {fmt(Number(summary.total_paid))}
            </p>
          </div>
          {summary.remaining !== null && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
                Remaining
              </p>
              <p
                className={`text-lg font-bold ${Number(summary.remaining) > 0 ? "text-[var(--text-warning)]" : "text-[var(--text-success)]"}`}
              >
                {fmt(Number(summary.remaining))}
              </p>
            </div>
          )}
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
              Payments
            </p>
            <p className="text-lg font-bold text-[var(--text-primary)]">
              {summary.payment_count}
            </p>
          </div>
          <div className="ml-auto">
            <Button size="sm" onClick={() => setShowForm(!showForm)}>
              {showForm ? (
                <>
                  <X className="h-3.5 w-3.5" /> Cancel
                </>
              ) : (
                <>
                  <Plus className="h-3.5 w-3.5" /> Record payment
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {showForm && (
        <PaymentForm
          entityType={entityType}
          entityId={entityId}
          lineItems={lineItems}
          onDone={() => {
            setShowForm(false);
            invalidateAll();
          }}
        />
      )}

      {/* Per-line payment breakdown */}
      {lineItems &&
        lineItems.length > 0 &&
        lineSummary &&
        lineSummary.length > 0 && (
          <div className="rounded-[var(--radius-lg)] border border-[var(--border-default)] bg-[var(--surface-base)] overflow-hidden">
            <div className="px-3 py-2 bg-[var(--surface-sunken)]/50 border-b border-[var(--border-subtle)]">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
                Per-Line Payment Status
              </p>
            </div>
            <div className="divide-y divide-[var(--border-subtle)]">
              {lineItems.map((li) => {
                const ls = lineSummaryMap.get(li.id);
                const allocated = ls ? Number(ls.total_allocated) : 0;
                const lineTotal =
                  Number(li.line_total || 0) ||
                  Number(li.total_price || 0) ||
                  Number(li.sale_price || 0);
                const remaining = lineTotal - allocated;
                const paidPct =
                  lineTotal > 0
                    ? Math.min((allocated / lineTotal) * 100, 100)
                    : 0;
                const label =
                  li.asset_name || li.description || li.id.slice(0, 8);
                return (
                  <div key={li.id} className="px-3 py-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-medium text-[var(--text-primary)] truncate">
                        {label}
                      </span>
                      <span
                        className={`text-xs tabular-nums font-semibold shrink-0 ${remaining <= 0 ? "text-[var(--text-success)]" : "text-[var(--text-warning)]"}`}
                      >
                        {allocated > 0
                          ? `${fmt(allocated)} / ${fmt(lineTotal)}`
                          : fmt(lineTotal)}
                      </span>
                    </div>
                    {allocated > 0 && (
                      <div className="mt-1 h-1.5 rounded-full bg-[var(--surface-sunken)] overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${paidPct >= 100 ? "bg-[var(--color-success)]" : "bg-[var(--color-interactive-primary)]"}`}
                          style={{ width: `${paidPct}%` }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

      {/* Payment history */}
      {isLoading ? (
        <LoadingSpinner className="py-4" />
      ) : (payments || []).length > 0 ? (
        <div className="space-y-1.5">
          {(payments || []).map((p) => {
            const Icon = METHOD_ICONS[p.payment_method] || DollarSign;
            const isFundedFromCredit =
              p.credit_account_id != null ||
              p.details?.funded_from_credit_account === true;
            return (
              <Card key={p.id} padding="sm" className="hover-lift">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--fill-success)] text-[var(--text-success)]">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-[var(--text-primary)]">
                        {fmt(Number(p.amount))}
                      </span>
                      <Badge variant="info">
                        {METHOD_LABELS[p.payment_method] || p.payment_method}
                      </Badge>
                      {isFundedFromCredit && (
                        <Badge variant="warning">
                          via{" "}
                          {p.details?.credit_account_name != null
                            ? String(p.details.credit_account_name)
                            : "credit account"}
                        </Badge>
                      )}
                      <Badge
                        variant={
                          p.status === "completed"
                            ? "success"
                            : p.status === "pending"
                              ? "warning"
                              : "danger"
                        }
                      >
                        {p.status}
                      </Badge>
                    </div>
                    <div className="mt-0.5 flex items-center gap-2 text-xs text-[var(--text-tertiary)] flex-wrap">
                      <span>
                        {new Date(p.payment_date).toLocaleDateString()}
                      </span>
                      {p.details?.check_number != null && (
                        <span>Check #{String(p.details.check_number)}</span>
                      )}
                      {p.details?.bank_name != null && (
                        <span>{String(p.details.bank_name)}</span>
                      )}
                      {p.details?.wire_reference != null && (
                        <span>Ref: {String(p.details.wire_reference)}</span>
                      )}
                      {p.details?.ach_confirmation != null && (
                        <span>ACH: {String(p.details.ach_confirmation)}</span>
                      )}
                      {p.details?.card_last4 != null && (
                        <span>****{String(p.details.card_last4)}</span>
                      )}
                      {p.details?.card_brand != null && (
                        <span>{String(p.details.card_brand)}</span>
                      )}
                      {p.details?.finance_company != null && (
                        <span>{String(p.details.finance_company)}</span>
                      )}
                      {p.notes && <span className="italic">{p.notes}</span>}
                    </div>
                  </div>
                  <button
                    onClick={() => deleteMutation.mutate(p.id)}
                    title="Remove payment"
                    className="shrink-0 flex h-7 w-7 items-center justify-center rounded-[var(--radius-sm)] text-[var(--text-tertiary)] hover:text-[var(--text-danger)] hover:bg-[var(--fill-danger)] transition-colors"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
                {p.allocations && p.allocations.length > 0 && lineItems && (
                  <div className="mt-2 pt-2 border-t border-[var(--border-subtle)]">
                    <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)] mb-1">
                      Line Allocations
                    </p>
                    {p.allocations.map((a) => {
                      const li = lineItems.find((l) => l.id === a.line_id);
                      return (
                        <div
                          key={a.id}
                          className="flex items-center justify-between py-0.5"
                        >
                          <span className="text-[11px] text-[var(--text-secondary)] truncate mr-2">
                            {li
                              ? li.asset_name ||
                                li.description ||
                                li.id.slice(0, 8)
                              : a.line_id.slice(0, 8)}
                          </span>
                          <span className="text-[11px] tabular-nums font-medium text-[var(--text-primary)] shrink-0">
                            {fmt(Number(a.amount))}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      ) : (
        <p className="text-center text-xs text-[var(--text-tertiary)] py-4">
          No payments recorded yet
        </p>
      )}
    </div>
  );
}

function PaymentForm({
  entityType,
  entityId,
  lineItems,
  onDone,
}: {
  entityType: string;
  entityId: string;
  lineItems?: LineItem[];
  onDone: () => void;
}) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("cash");
  const [notes, setNotes] = useState("");
  const [paymentDate, setPaymentDate] = useState(
    new Date().toISOString().slice(0, 10),
  );

  // Line-level allocation.
  const [allocateToLines, setAllocateToLines] = useState(false);
  const [lineAllocations, setLineAllocations] = useState<
    Record<string, string>
  >({});

  const allocatedTotal = Object.values(lineAllocations).reduce(
    (s, v) => s + (parseFloat(v) || 0),
    0,
  );
  const paymentAmount = parseFloat(amount) || 0;
  const unallocated = paymentAmount - allocatedTotal;

  // Funding source — independent of payment method.
  const [useCreditAccount, setUseCreditAccount] = useState(false);
  const [creditAccountId, setCreditAccountId] = useState("");

  // Method-specific fields.
  const [checkNumber, setCheckNumber] = useState("");
  const [bankName, setBankName] = useState("");
  const [wireRef, setWireRef] = useState("");
  const [achConfirmation, setAchConfirmation] = useState("");
  const [cardLast4, setCardLast4] = useState("");
  const [cardBrand, setCardBrand] = useState("");
  const [authCode, setAuthCode] = useState("");
  const [financeCompany, setFinanceCompany] = useState("");
  const [appNumber, setAppNumber] = useState("");
  const [termMonths, setTermMonths] = useState("");
  const [financeRate, setFinanceRate] = useState("");
  const [otherDesc, setOtherDesc] = useState("");

  // Credit accounts (always fetched).
  const { data: creditAccounts } = useQuery<{ items: CreditAccount[] }>({
    queryKey: ["credit-accounts"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/credit-accounts?page_size=100")).data,
  });

  const recordMutation = useMutation({
    mutationFn: async () => {
      const details: Record<string, unknown> = {};
      if (method === "check") {
        if (checkNumber) details.check_number = checkNumber;
        if (bankName) details.bank_name = bankName;
      }
      if (method === "wire") {
        if (wireRef) details.wire_reference = wireRef;
      }
      if (method === "ach") {
        if (achConfirmation) details.ach_confirmation = achConfirmation;
      }
      if (method === "credit_card") {
        if (cardLast4) details.card_last4 = cardLast4;
        if (cardBrand) details.card_brand = cardBrand;
        if (authCode) details.authorization_code = authCode;
      }
      if (method === "financing") {
        if (financeCompany) details.finance_company = financeCompany;
        if (appNumber) details.application_number = appNumber;
        if (termMonths) details.term_months = parseInt(termMonths);
        if (financeRate) details.rate = parseFloat(financeRate);
      }
      if (method === "other") {
        if (otherDesc) details.description = otherDesc;
      }

      const allocations =
        allocateToLines && lineItems
          ? Object.entries(lineAllocations)
              .filter(([, v]) => parseFloat(v) > 0)
              .map(([lineId, v]) => ({
                line_id: lineId,
                amount: parseFloat(v),
              }))
          : undefined;

      return (
        await apiClient.post("/api/v1/payments", {
          entity_type: entityType,
          entity_id: entityId,
          amount: parseFloat(amount),
          payment_method: method,
          details: Object.keys(details).length > 0 ? details : undefined,
          credit_account_id:
            useCreditAccount && creditAccountId ? creditAccountId : undefined,
          notes: notes || undefined,
          payment_date: new Date(paymentDate).toISOString(),
          allocations:
            allocations && allocations.length > 0 ? allocations : undefined,
        })
      ).data;
    },
    onSuccess: () => {
      toast.success("Payment recorded");
      onDone();
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      toast.error(
        "Failed to record payment",
        msg || "Please check the form and try again.",
      );
    },
  });

  const activeAccounts = (creditAccounts?.items || []).filter(
    (a: CreditAccount) => a.is_active,
  );

  return (
    <Card>
      <h3 className="text-sm font-medium text-[var(--text-primary)] mb-3">
        Record payment
      </h3>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          recordMutation.mutate();
        }}
        className="space-y-3"
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Input
            label="Amount"
            type="number"
            step="0.01"
            min="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
            placeholder="0.00"
          />
          <div>
            <label className="block text-sm font-medium text-[var(--text-primary)]">
              Payment method
            </label>
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              className="mt-1.5 block w-full h-9 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)]"
            >
              {Object.entries(METHOD_LABELS).map(([val, label]) => (
                <option key={val} value={val}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <Input
            label="Payment date"
            type="date"
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
          />
        </div>

        {/* Method-specific fields */}
        {method === "check" && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input
              label="Check number"
              value={checkNumber}
              onChange={(e) => setCheckNumber(e.target.value)}
              placeholder="1234"
            />
            <Input
              label="Bank name"
              value={bankName}
              onChange={(e) => setBankName(e.target.value)}
              placeholder="First National Bank"
            />
          </div>
        )}
        {method === "wire" && (
          <Input
            label="Wire reference"
            value={wireRef}
            onChange={(e) => setWireRef(e.target.value)}
            placeholder="WIRE-2026-04-21-001"
          />
        )}
        {method === "ach" && (
          <Input
            label="ACH confirmation"
            value={achConfirmation}
            onChange={(e) => setAchConfirmation(e.target.value)}
            placeholder="ACH123456789"
          />
        )}
        {method === "credit_card" && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Input
              label="Card last 4"
              value={cardLast4}
              onChange={(e) => setCardLast4(e.target.value)}
              maxLength={4}
              placeholder="4242"
            />
            <Input
              label="Card brand"
              value={cardBrand}
              onChange={(e) => setCardBrand(e.target.value)}
              placeholder="Visa"
            />
            <Input
              label="Auth code"
              value={authCode}
              onChange={(e) => setAuthCode(e.target.value)}
              placeholder="AUTH-123"
            />
          </div>
        )}
        {method === "financing" && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input
              label="Finance company"
              value={financeCompany}
              onChange={(e) => setFinanceCompany(e.target.value)}
              placeholder="LEAF Commercial Capital"
            />
            <Input
              label="Application #"
              value={appNumber}
              onChange={(e) => setAppNumber(e.target.value)}
              placeholder="APP-2026-001"
            />
            <Input
              label="Term (months)"
              type="number"
              value={termMonths}
              onChange={(e) => setTermMonths(e.target.value)}
              placeholder="36"
            />
            <Input
              label="Rate (%)"
              type="number"
              step="0.01"
              value={financeRate}
              onChange={(e) => setFinanceRate(e.target.value)}
              placeholder="7.25"
            />
          </div>
        )}
        {method === "other" && (
          <Input
            label="Description"
            value={otherDesc}
            onChange={(e) => setOtherDesc(e.target.value)}
            placeholder="Describe the payment method"
          />
        )}

        {/* Funding source — credit account (independent of method) */}
        <div className="rounded-[var(--radius-lg)] border border-[var(--border-default)] px-4 py-3 bg-[var(--surface-base)]">
          <label className="flex items-center justify-between cursor-pointer">
            <div>
              <p className="text-sm font-medium text-[var(--text-primary)]">
                Funded from a credit account
              </p>
              <p className="text-[11px] text-[var(--text-tertiary)]">
                Enable if this payment draws from a line of credit, credit card
                account, or loan
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={useCreditAccount}
              onClick={() => {
                setUseCreditAccount(!useCreditAccount);
                if (useCreditAccount) setCreditAccountId("");
              }}
              className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors duration-200 ${
                useCreditAccount
                  ? "bg-[var(--color-interactive-primary)]"
                  : "bg-[var(--border-default)]"
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-[240ms] mt-0.5 ${
                  useCreditAccount ? "translate-x-4" : "translate-x-0.5"
                }`}
                style={{
                  transitionTimingFunction: "cubic-bezier(0.34, 1.56, 0.64, 1)",
                }}
              />
            </button>
          </label>
          {useCreditAccount && (
            <div className="mt-3">
              <label className="block text-sm font-medium text-[var(--text-primary)]">
                Credit account
              </label>
              <select
                value={creditAccountId}
                onChange={(e) => setCreditAccountId(e.target.value)}
                required
                className="mt-1.5 block w-full h-9 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)]"
              >
                <option value="">Select credit account...</option>
                {activeAccounts.map((a: CreditAccount) => (
                  <option key={a.id} value={a.id}>
                    {a.name} ({a.account_type.replace(/_/g, " ")}) — Balance: $
                    {Number(a.current_balance).toFixed(2)}
                    {a.credit_limit
                      ? ` / Limit: $${Number(a.credit_limit).toFixed(2)}`
                      : ""}
                  </option>
                ))}
              </select>
              {activeAccounts.length === 0 && (
                <p className="mt-1 text-xs text-[var(--text-tertiary)]">
                  No active credit accounts. Create one in Procurement → Credit
                  accounts.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Line-level allocation (optional) */}
        {lineItems && lineItems.length > 0 && (
          <div className="rounded-[var(--radius-lg)] border border-[var(--border-default)] px-4 py-3 bg-[var(--surface-base)]">
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <p className="text-sm font-medium text-[var(--text-primary)]">
                  Allocate to specific line items
                </p>
                <p className="text-[11px] text-[var(--text-tertiary)]">
                  Assign payment amounts to individual items on this order
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={allocateToLines}
                onClick={() => {
                  setAllocateToLines(!allocateToLines);
                  if (allocateToLines) setLineAllocations({});
                }}
                className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors duration-200 ${
                  allocateToLines
                    ? "bg-[var(--color-interactive-primary)]"
                    : "bg-[var(--border-default)]"
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-[240ms] mt-0.5 ${
                    allocateToLines ? "translate-x-4" : "translate-x-0.5"
                  }`}
                  style={{
                    transitionTimingFunction:
                      "cubic-bezier(0.34, 1.56, 0.64, 1)",
                  }}
                />
              </button>
            </label>
            {allocateToLines && (
              <div className="mt-3 space-y-2">
                {lineItems.map((li) => {
                  const lineTotal =
                    Number(li.line_total || 0) ||
                    Number(li.total_price || 0) ||
                    Number(li.sale_price || 0);
                  const label =
                    li.asset_name || li.description || li.id.slice(0, 8);
                  return (
                    <div key={li.id} className="flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-[var(--text-primary)] truncate">
                          {label}
                        </p>
                        <p className="text-[10px] text-[var(--text-tertiary)]">
                          {fmt(lineTotal)}
                        </p>
                      </div>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={lineAllocations[li.id] || ""}
                        onChange={(e) =>
                          setLineAllocations((prev) => ({
                            ...prev,
                            [li.id]: e.target.value,
                          }))
                        }
                        placeholder="0.00"
                        className="w-28 h-8 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-sm text-right tabular-nums text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setLineAllocations((prev) => ({
                            ...prev,
                            [li.id]: lineTotal.toFixed(2),
                          }));
                        }}
                        className="text-[10px] font-medium text-[var(--color-interactive-primary)] hover:underline shrink-0"
                        title="Set to line total"
                      >
                        Full
                      </button>
                    </div>
                  );
                })}
                <div className="flex items-center justify-between pt-2 border-t border-[var(--border-subtle)]">
                  <span className="text-[11px] text-[var(--text-secondary)]">
                    Allocated: {fmt(allocatedTotal)}
                  </span>
                  <span
                    className={`text-[11px] font-medium ${
                      Math.abs(unallocated) < 0.01
                        ? "text-[var(--text-success)]"
                        : "text-[var(--text-warning)]"
                    }`}
                  >
                    {Math.abs(unallocated) < 0.01
                      ? "Fully allocated"
                      : `${fmt(Math.abs(unallocated))} ${unallocated > 0 ? "unallocated" : "over-allocated"}`}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        <Input
          label="Notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional notes about this payment"
        />

        <div className="flex justify-end">
          <Button
            type="submit"
            size="sm"
            isLoading={recordMutation.isPending}
            disabled={
              allocateToLines &&
              paymentAmount > 0 &&
              Math.abs(unallocated) >= 0.01
            }
          >
            Record payment
          </Button>
        </div>
      </form>
    </Card>
  );
}

export default PaymentPanel;
